/**
 * DataGridTable (ADR-0010 — Airtable-style grid, slice G1).
 *
 * A spreadsheet-feel data grid built on **TanStack Table** (headless: it owns
 * the column/row model; the cell markup + inline editors are ours). Versus the
 * old `DataGrid` (kept for its reusable field editors + the row-expand dialog,
 * which we import here), this adds the things that make a grid read as Airtable:
 *
 *   - full **gridlines** (every cell bordered), compact density by default;
 *   - a leading **row-number** gutter (checkbox on hover / when selected);
 *   - **inline cell editing** for scalar fields (text / number / select /
 *     boolean / date / datetime) — click a cell, edit in place, commit on
 *     blur/Enter (a single-field PATCH; `rows.update` is partial server-side);
 *   - **inline "+"** affordances: a blank "+ row" under the body and a "+ field"
 *     trailing header column (admin only, via the `schema` prop);
 *   - complex fields (relation / relation_many / file / member / json) and
 *     computed columns stay read-only in the cell and open the **row-expand
 *     dialog** on click (slice G2 will make those inline too).
 *
 * The schema-edit affordances (header field menus + "+ field") only appear when
 * `schema` is supplied — same contract as `DataGrid`, so non-admin call sites
 * get a plain editable grid.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type CellContext,
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Check, Copy, Hash, Maximize2, Plus, Rows3, Rows4, Table2, Trash2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/page";
import { Pagination } from "@/components/ui/pagination";
import {
  ColumnHeader,
  FileCell,
  MemberCell,
  RowDialog,
  coerceValue,
  columnOptions,
  type FieldKind,
  type GridSchemaMenu,
  fieldKindForColumn,
  formatCell,
  relCellIds,
  relCellLabel,
  relCellLabelsMany,
} from "@/components/apps/DataGrid";
import { appsApi, type AppEnv, type ColumnOut, type Row } from "@/lib/api-apps";
import { isComputedColumn } from "@/lib/columnPayload";
import { pageSelectionState, togglePage, toggleSelected } from "@/lib/gridSelection";
import { usePaginationState } from "@/lib/pagination";

// Carry the field's metadata on its column def so cells can read it without
// rebuilding the column model on every edit-state change.
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends import("@tanstack/react-table").RowData, TValue> {
    col?: ColumnOut;
  }
}

/** Field kinds we edit inline in slice G1. The rest open the row-expand dialog. */
const INLINE_SCALAR: ReadonlySet<FieldKind> = new Set<FieldKind>([
  "text",
  "number",
  "select",
  "date",
  "datetime",
]);

/** Per-table render context handed to every cell via TanStack `meta`, so the
 *  column defs don't need rebuilding on every edit-state change. */
interface GridMeta {
  appId: string;
  dense: boolean;
  selected: Set<string>;
  toggleRow: (id: string) => void;
  editing: { rowId: string; colId: string } | null;
  beginEdit: (rowId: string, colId: string) => void;
  endEdit: () => void;
  commit: (rowId: string, col: ColumnOut, raw: string | boolean) => void;
  expandRow: (row: Row) => void;
}

/** The read-only display for a cell value (mirrors the old grid's renderers). */
function cellDisplay(appId: string, col: ColumnOut, row: Row): React.ReactNode {
  const kind = fieldKindForColumn(col);
  if (kind === "file") return <FileCell appId={appId} value={row[col.name]} />;
  if (kind === "member") return <MemberCell appId={appId} value={row[col.name]} />;
  if (kind === "relation") return relCellLabel(row, col.name) ?? formatCell(row[col.name]);
  if (kind === "relation_many") {
    const labels = relCellLabelsMany(row, col.name);
    if (labels.length) return labels.join(", ");
    const ids = relCellIds(row, col.name);
    return ids.length ? `${ids.length} linked` : "—";
  }
  return formatCell(row[col.name]);
}

/** The in-cell inline editor for a scalar field. Commits on blur/Enter, cancels
 *  on Escape. `select` renders the column's options; date/datetime use native
 *  pickers; everything else is a text/number input. */
function InlineEditor({
  kind,
  options,
  initial,
  onCommit,
  onCancel,
}: {
  kind: FieldKind;
  options: string[];
  initial: unknown;
  onCommit: (raw: string) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = React.useState(initial == null ? "" : String(initial));
  const cls =
    "h-full w-full border-0 bg-surface px-2 py-1 text-sm outline-none ring-1 ring-inset ring-accent/60";

  if (kind === "select") {
    return (
      <select
        autoFocus
        className={cls}
        value={draft}
        onChange={(e) => onCommit(e.target.value)}
        onBlur={onCancel}
        onKeyDown={(e) => e.key === "Escape" && onCancel()}
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    );
  }

  const inputType = kind === "number" ? "number" : kind === "date" ? "date" : kind === "datetime" ? "datetime-local" : "text";
  return (
    <input
      autoFocus
      type={inputType}
      className={cls}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => onCommit(draft)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onCommit(draft);
        else if (e.key === "Escape") onCancel();
      }}
    />
  );
}

/** A single data cell: inline editor when active, an immediate toggle for
 *  booleans, a click-to-expand button for complex/computed fields, else a
 *  click-to-edit value. */
function DataCell({ ctx }: { ctx: CellContext<Row, unknown> }) {
  const meta = ctx.table.options.meta as GridMeta;
  const col = ctx.column.columnDef.meta?.col;
  if (!col) return null;
  const row = ctx.row.original;
  const rowId = String(row.id);
  const kind = fieldKindForColumn(col);
  const computed = isComputedColumn(col);
  const editing = meta.editing?.rowId === rowId && meta.editing?.colId === col.id;

  if (kind === "boolean" && !computed) {
    return (
      <input
        type="checkbox"
        className="mx-2"
        checked={Boolean(row[col.name])}
        onChange={(e) => meta.commit(rowId, col, e.target.checked)}
      />
    );
  }

  if (computed || !INLINE_SCALAR.has(kind)) {
    // Complex (relation/file/member/json) + computed → read-only, click expands.
    return (
      <button
        type="button"
        className="block w-full truncate px-2 py-1 text-left"
        onClick={() => meta.expandRow(row)}
        title={computed ? undefined : "Apri riga per modificare"}
      >
        {cellDisplay(meta.appId, col, row)}
      </button>
    );
  }

  if (editing) {
    return (
      <InlineEditor
        kind={kind}
        options={columnOptions(col)}
        initial={row[col.name]}
        onCommit={(raw) => meta.commit(rowId, col, raw)}
        onCancel={meta.endEdit}
      />
    );
  }

  return (
    <div
      className="block w-full cursor-text truncate px-2 py-1"
      onClick={() => meta.beginEdit(rowId, col.id)}
    >
      {formatCell(row[col.name])}
    </div>
  );
}

/** Copy a row's `id` to the clipboard, with a brief ✓ confirmation. The system
 *  `id` column is hidden from the grid by default, so this is how the operator
 *  grabs a row's UUID (e.g. to test a per-id REST endpoint) without going to the
 *  DB. Two shapes: a hover ICON in the row gutter, and a full-width CELL for the
 *  toggleable `id` column. `stopPropagation` so a click never selects/edits. */
function CopyId({ id, variant, className }: { id: string; variant: "icon" | "cell"; className?: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);
  const copy = (e: React.MouseEvent) => {
    e.stopPropagation();
    void navigator.clipboard?.writeText(id);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };
  const Icon = copied ? Check : Copy;
  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={copy}
        title={t("apps.copyId", "Copy ID")}
        aria-label={t("apps.copyId", "Copy ID")}
        className={`inline-flex h-5 w-5 items-center justify-center rounded text-muted-foreground opacity-0 transition hover:bg-elevated hover:text-foreground group-hover/row:opacity-100 ${copied ? "text-success opacity-100" : ""} ${className ?? ""}`}
      >
        <Icon className="h-3 w-3" strokeWidth={1.5} />
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={t("apps.copyId", "Copy ID")}
      className="flex h-full w-full items-center gap-1.5 px-2 text-left font-mono text-xs text-muted-foreground transition hover:text-foreground"
    >
      <span className="truncate">{id}</span>
      <Icon className={`h-3 w-3 shrink-0 ${copied ? "text-success" : ""}`} strokeWidth={1.5} />
    </button>
  );
}

export function DataGridTable({
  appId,
  tableId,
  columns,
  env = "dev",
  schema,
  toolbarLeft,
  toolbarRight,
}: {
  appId: string;
  tableId: string;
  columns: ColumnOut[];
  env?: AppEnv;
  /** Admin schema-edit affordances (header menus + "+ field"). */
  schema?: GridSchemaMenu;
  /** Host-injected controls for the LEFT of the unified toolbar (e.g. the view
   *  pills from `DataSection`). */
  toolbarLeft?: React.ReactNode;
  /** Host-injected controls for the RIGHT cluster, before the grid's own
   *  density / field / add-row controls (e.g. column-visibility + save-view). */
  toolbarRight?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { page, pageSize, setPage, setPageSize } = usePaginationState();
  const [dense, setDense] = React.useState(true); // compact by default, Airtable-style
  const [showId, setShowId] = React.useState(false); // reveal the system `id` column (off by default)
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [editing, setEditing] = React.useState<{ rowId: string; colId: string } | null>(null);
  const [expand, setExpand] = React.useState<{ open: boolean; row: Row | null }>({
    open: false,
    row: null,
  });

  const rowsQ = useQuery({
    queryKey: ["apps", appId, "tables", tableId, "rows", env, page, pageSize],
    queryFn: () => appsApi.queryRows(appId, tableId, { page, page_size: pageSize }, env),
  });
  const rows = rowsQ.data?.rows ?? [];
  const total = rowsQ.data?.total ?? 0;
  const pageIds = rows.map((r) => String(r.id));
  const headState = pageSelectionState(pageIds, selected);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["apps", appId, "tables", tableId, "rows"] });

  const updateCell = useMutation({
    mutationFn: (v: { rowId: string; data: Row }) =>
      appsApi.updateRow(appId, tableId, v.rowId, v.data, env),
    onSuccess: invalidate,
  });
  const bulkDel = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) await appsApi.deleteRow(appId, tableId, id, env);
    },
    onSuccess: () => {
      invalidate();
      setSelected(new Set());
    },
  });

  const meta: GridMeta = {
    appId,
    dense,
    selected,
    toggleRow: (id) => setSelected((s) => toggleSelected(s, id)),
    editing,
    beginEdit: (rowId, colId) => setEditing({ rowId, colId }),
    endEdit: () => setEditing(null),
    commit: (rowId, col, raw) => {
      const kind = fieldKindForColumn(col);
      const value = kind === "boolean" ? Boolean(raw) : coerceValue(kind, raw, col.required);
      setEditing(null);
      updateCell.mutate({ rowId, data: { [col.name]: value } });
    },
    expandRow: (row) => setExpand({ open: true, row }),
  };

  // TanStack column model: a leading select/row-number gutter, one column per
  // field (carrying its ColumnOut in `meta.col`), and a trailing "+ field".
  const tableColumns = React.useMemo<ColumnDef<Row, unknown>[]>(() => {
    const defs: ColumnDef<Row, unknown>[] = [
      {
        id: "__gutter",
        header: () => null, // header checkbox rendered manually (needs head state)
        cell: (ctx) => {
          const rid = String(ctx.row.original.id ?? ctx.row.index);
          const sel = (ctx.table.options.meta as GridMeta).selected.has(rid);
          const hasId = ctx.row.original.id != null;
          return (
            <div className="relative flex h-full items-center justify-center">
              <span
                className={`text-xs text-muted-foreground tabular-nums ${sel ? "opacity-0" : "group-hover/row:opacity-0"}`}
              >
                {ctx.row.index + 1}
              </span>
              <input
                type="checkbox"
                aria-label={t("apps.selectRow", "Select row")}
                checked={sel}
                onChange={() => (ctx.table.options.meta as GridMeta).toggleRow(rid)}
                className={`absolute left-1.5 ${sel ? "opacity-100" : "opacity-0 group-hover/row:opacity-100"}`}
              />
              {hasId && <CopyId id={rid} variant="icon" className="absolute right-0.5" />}
            </div>
          );
        },
      },
      ...(showId
        ? [
            {
              id: "__id",
              accessorFn: (row: Row) => row.id,
              header: () => <span className="font-mono text-xs">id</span>,
              cell: (ctx: CellContext<Row, unknown>) => <CopyId id={String(ctx.row.original.id ?? "")} variant="cell" />,
            } as ColumnDef<Row, unknown>,
          ]
        : []),
      ...columns.map<ColumnDef<Row, unknown>>((c) => ({
        id: c.id,
        accessorFn: (row) => row[c.name],
        meta: { col: c },
        header: () => (schema ? <ColumnHeader col={c} schema={schema} /> : c.name),
        cell: (ctx) => <DataCell ctx={ctx} />,
      })),
    ];
    return defs;
  }, [columns, schema, showId, t]);

  const table = useReactTable({
    data: rows,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    meta,
  });

  const cellH = dense ? "h-8" : "h-10";
  const totalCols = columns.length + 1 + (schema ? 1 : 0) + (showId ? 1 : 0);

  return (
    <section className="space-y-2">
      {/* Unified control bar — view pills (host-injected) on the left, a tight
          icon-cluster on the right (visibility / save-view ‖ density · +field ·
          +row). One compact strip, not two scattered toolbars. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 rounded-lg border border-border bg-surface px-2 py-1.5">
        <div className="flex min-w-0 flex-wrap items-center gap-1">{toolbarLeft}</div>
        <div className="flex items-center gap-0.5">
          {toolbarRight}
          {toolbarRight && <span className="mx-1 h-5 w-px bg-border" />}
          <button
            type="button"
            title={t("apps.toggleIdColumn", "Show/hide the system ID column")}
            aria-label={t("apps.showId", "Show ID")}
            aria-pressed={showId}
            onClick={() => setShowId((s) => !s)}
            className={`inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition hover:bg-elevated hover:text-foreground ${showId ? "bg-elevated text-foreground" : "text-muted-foreground"}`}
          >
            <Hash className="h-4 w-4" strokeWidth={1.5} />
            <span className="hidden md:inline">{t("apps.showId", "Show ID")}</span>
          </button>
          <button
            type="button"
            title={dense ? t("apps.comfortable", "Comfortable") : t("apps.compact", "Compact")}
            aria-label={dense ? t("apps.comfortable", "Comfortable") : t("apps.compact", "Compact")}
            onClick={() => setDense((d) => !d)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition hover:bg-elevated hover:text-foreground"
          >
            {dense ? <Rows4 className="h-4 w-4" strokeWidth={1.5} /> : <Rows3 className="h-4 w-4" strokeWidth={1.5} />}
          </button>
          {schema && (
            <button
              type="button"
              onClick={schema.onAddField}
              className="inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium text-muted-foreground transition hover:bg-elevated hover:text-foreground"
            >
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              {t("apps.addField", "Field")}
            </button>
          )}
          <button
            type="button"
            onClick={() => setExpand({ open: true, row: null })}
            className="ml-1 inline-flex h-8 items-center gap-1.5 rounded-md bg-accent px-2.5 text-sm font-medium text-accent-foreground transition hover:opacity-90"
          >
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            {t("apps.addRow", "Add row")}
          </button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-accent/40 bg-accent/5 px-3 py-2 text-sm">
          <span className="font-medium">
            {t("apps.nSelected", "{{n}} selected", { n: selected.size })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="text-danger"
            disabled={bulkDel.isPending}
            onClick={() => {
              const ids = [...selected];
              if (confirm(t("apps.confirmBulkDelete", "Delete {{n}} selected rows?", { n: ids.length }))) {
                bulkDel.mutate(ids);
              }
            }}
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            {bulkDel.isPending ? t("apps.deleting", "Deleting…") : t("apps.deleteSelected", "Delete selected")}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
            {t("common.clear", "Clear")}
          </Button>
        </div>
      )}

      {rows.length === 0 && !rowsQ.isLoading ? (
        <EmptyState
          icon={Table2}
          title={t("apps.noRows", "No rows yet")}
          description={t("apps.noRowsDescription", "Add a row, or write to this table from a workflow.")}
        />
      ) : (
        <>
          <div className="overflow-auto rounded-lg border border-border">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-surface">
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {/* gutter header: select-all */}
                    <th className="w-14 border-b border-r border-border px-0 text-center align-middle">
                      <input
                        type="checkbox"
                        aria-label={t("apps.selectAll", "Select all on this page")}
                        checked={headState === "all"}
                        ref={(el) => {
                          if (el) el.indeterminate = headState === "some";
                        }}
                        onChange={() => setSelected((s) => togglePage(pageIds, s))}
                      />
                    </th>
                    {hg.headers
                      .filter((h) => h.column.id !== "__gutter")
                      .map((h) => (
                        <th
                          key={h.id}
                          className="min-w-[8rem] border-b border-r border-border px-2 py-1.5 text-left font-medium text-muted-foreground"
                        >
                          {flexRender(h.column.columnDef.header, h.getContext())}
                        </th>
                      ))}
                    {schema && (
                      <th className="w-10 border-b border-border text-center">
                        <button
                          type="button"
                          aria-label={t("apps.addField", "Field")}
                          title={t("apps.addField", "Field")}
                          className="mx-auto flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-elevated hover:text-foreground"
                          onClick={schema.onAddField}
                        >
                          <Plus className="h-4 w-4" strokeWidth={1.5} />
                        </button>
                      </th>
                    )}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((r) => {
                  const rid = String(r.original.id ?? r.index);
                  return (
                    <tr
                      key={r.id}
                      className={`group/row ${selected.has(rid) ? "bg-accent/5" : "hover:bg-muted/30"}`}
                    >
                      {r.getVisibleCells().map((cell) =>
                        cell.column.id === "__gutter" ? (
                          <td key={cell.id} className={`w-14 border-b border-r border-border ${cellH}`}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ) : (
                          <td
                            key={cell.id}
                            className={`max-w-[22rem] border-b border-r border-border p-0 align-middle ${cellH}`}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ),
                      )}
                      {schema && (
                        <td className={`w-10 border-b border-border text-center ${cellH}`}>
                          <button
                            type="button"
                            aria-label={t("apps.expandRow", "Expand row")}
                            className="mx-auto flex h-6 w-6 items-center justify-center rounded text-muted-foreground opacity-0 hover:bg-elevated hover:text-foreground group-hover/gutter:opacity-100"
                            onClick={() => setExpand({ open: true, row: r.original })}
                          >
                            <Maximize2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {/* Inline "+ row" — opens the create dialog (handles required fields). */}
                <tr>
                  <td colSpan={totalCols} className="border-border">
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                      onClick={() => setExpand({ open: true, row: null })}
                    >
                      <Plus className="h-4 w-4" strokeWidth={1.5} />
                      {t("apps.addRow", "Add row")}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      )}

      <RowDialog
        appId={appId}
        tableId={tableId}
        columns={columns}
        env={env}
        open={expand.open}
        row={expand.row}
        onOpenChange={(open) => setExpand((e) => ({ ...e, open }))}
      />
    </section>
  );
}
